import { type RuleCandidate } from '@components/Customization/PoiTypes/RuleEditor';
import { arrayMove } from '@dnd-kit/sortable';
import { withType } from '@lib/customization/fieldDraft';
import { answerKeysOf, blockEnd, buildTree, followUpSlot, triggerOf, writeVisibility } from '@lib/customization/poiDesign';
import {
  conditionComplete, conditionProblem, isAlwaysAnswered, STEP_STATUS_ID, stepStatuses, type ConditionProblem,
} from '@lib/customization/poiRules';
import { noticeLabelKeys } from '@lib/spproject/inspectionStatus';
import {
  hasText, newEntityId, type FormInputType, type LocalizedText, type PoiField, type PoiFieldCondition,
  type PoiFieldGroup, type PoiType, type ResolvedItem,
} from '@store/api/poiTypeEndpoints';

/**
 * The POI type designer's working copy and every edit made to it.
 *
 * Each edit is a pure function from one draft to the next, so the designer component only
 * wires them to state. The draft has the shape the API stores, and is saved as one PUT.
 */

export interface Draft {
  /** Edited in the JSON view only; the canvas draws the fields. */
  name?: LocalizedText;
  description?: string;
  fields: PoiField[];
  groups: PoiFieldGroup[];
}

/** A field list: `'type'` for the fields directly on the type, or a group's index. */
export type Container = 'type' | number;

export interface Position {
  container: Container;
  index: number;
}

export const draftOf = (poiType: PoiType): Draft => ({
  name: poiType.name, description: poiType.description, fields: poiType.fields, groups: poiType.groups,
});

export const fieldsIn = (draft: Draft, container: Container): PoiField[] =>
  (container === 'type' ? draft.fields : draft.groups[container].fields);

const withFieldsIn = (draft: Draft, container: Container, fields: PoiField[]): Draft => (container === 'type'
  ? { ...draft, fields }
  : { ...draft, groups: draft.groups.map((group, index) => (index === container ? { ...group, fields } : group)) });

export const allFieldsOf = (draft: Draft): PoiField[] => [...draft.fields, ...draft.groups.flatMap((group) => group.fields)];

export const locate = (draft: Draft, id: string): (Position & { field: PoiField }) | null => {
  const containers: Container[] = ['type', ...draft.groups.map((_, index) => index)];

  for (const container of containers) {
    const index = fieldsIn(draft, container).findIndex((field) => field.id === id);

    if (index >= 0) return { container, index, field: fieldsIn(draft, container)[index] };
  }

  return null;
};

export const newField = (type: FormInputType): PoiField => withType({
  id: newEntityId(), label: {}, type: 'Text', visible: true, required: false, readOnly: false, showOnReports: true,
}, type);

/**
 * A follow-up: the stored pair the contract describes, hidden at the base with one rule
 * showing it. Asked, it is required (where that means anything); printed, only when asked.
 */
export const newFollowUp = (
  type: FormInputType, condition: PoiFieldCondition, when?: { match: 'all' | 'any'; conditions: PoiFieldCondition[] },
): PoiField => writeVisibility(
  newField(type),
  // A sibling is asked exactly when its sibling is: all of that one's conditions, joined its way.
  {
    mode: 'when', show: true, match: when?.match ?? 'all',
    conditions: when ? when.conditions.map((each) => ({ ...each })) : [condition],
  },
  'whenAsked',
  !isAlwaysAnswered(type),
);

export const newGroup = (): PoiFieldGroup => ({ id: newEntityId(), name: {}, visible: true, fields: [] });

/** A copy with fresh ids for itself and its choices; anything pointing at the original still does. */
export const copyOf = (field: PoiField): PoiField => {
  const ids = new Map((field.options ?? []).map((option) => [option.id, newEntityId()]));
  const remap = (id: string) => ids.get(id) ?? id;

  return {
    ...field,
    id: newEntityId(),
    options: field.options?.map((option) => ({ ...option, id: option.id && remap(option.id) })),
    value: Array.isArray(field.value) ? field.value.map(remap) : typeof field.value === 'string' ? remap(field.value) : field.value,
    rules: field.rules?.map((rule) => ({ ...rule, id: newEntityId() })),
  };
};

export const insertField = (draft: Draft, { container, index }: Position, field: PoiField): Draft => {
  const fields = [...fieldsIn(draft, container)];

  fields.splice(index, 0, field);

  return withFieldsIn(draft, container, fields);
};

/** Right after the field `id` and everything drawn under it. */
export const afterBlockOf = (draft: Draft, id: string): Position | null => {
  const at = locate(draft, id);

  return at && { container: at.container, index: blockEnd(fieldsIn(draft, at.container), at.index) };
};

/** Where a follow-up of `triggerId` for `condition` goes; see `followUpSlot`. */
export const followUpPosition = (draft: Draft, triggerId: string, condition: PoiFieldCondition): Position => {
  const at = locate(draft, triggerId);

  // A trigger inherited from an ancestor is in none of this type's lists: the closest the
  // follow-up can sit is the top of the type's own fields.
  if (!at) return { container: 'type', index: 0 };

  return { container: at.container, index: followUpSlot(fieldsIn(draft, at.container), at.index, [condition]) };
};

export const updateField = (draft: Draft, id: string, next: PoiField): Draft => {
  const at = locate(draft, id);

  return at
    ? withFieldsIn(draft, at.container, fieldsIn(draft, at.container).map((field, index) => (index === at.index ? next : field)))
    : draft;
};

/** Merges changes into the field as the draft holds it now. */
export const patchField = (draft: Draft, id: string, changes: Partial<PoiField>): Draft => {
  const at = locate(draft, id);

  return at ? updateField(draft, id, { ...at.field, ...changes }) : draft;
};

export const patchGroup = (draft: Draft, id: string, changes: Partial<PoiFieldGroup>): Draft =>
  ({ ...draft, groups: draft.groups.map((group) => (group.id === id ? { ...group, ...changes } : group)) });

/** Every field that only exists because of `id`: its follow-ups, theirs, and so on. */
export const followUpsOf = (draft: Draft, id: string): string[] => {
  const fields = allFieldsOf(draft);
  const found: string[] = [];
  let parents = [id];

  while (parents.length) {
    parents = fields
      .filter((field) => field.id && !found.includes(field.id) && parents.includes(triggerOf(field) ?? ''))
      .map((field) => field.id!);
    found.push(...parents);
  }

  return found;
};

/** Every rule with its conditions on `ids` taken out, and any rule left without one dropped. */
const withoutConditionsOn = <T extends PoiField | PoiFieldGroup>(entry: T, ids: string[]): T => {
  if (!entry.rules) return entry;

  const rules = entry.rules
    .map((rule) => ({ ...rule, when: { ...rule.when, conditions: rule.when.conditions.filter((condition) => !ids.includes(condition.fieldId)) } }))
    .filter((rule) => rule.when.conditions.length);

  return { ...entry, rules: rules.length ? rules : undefined };
};

/**
 * Removes fields, and every condition that pointed at them: the delete dialog says so first.
 * The fields in `release` stay, as ordinary always-asked fields: they were follow-ups of a
 * removed field, and losing their condition would otherwise leave them hidden for good.
 */
export const removeFields = (draft: Draft, ids: string[], release: string[] = []): Draft => {
  const strip = (fields: PoiField[]) => fields
    .filter((field) => !ids.includes(field.id ?? ''))
    .map((field) => (release.includes(field.id ?? '')
      ? writeVisibility(field, { mode: 'always' }, field.showOnReports || field.rules?.[0]?.then.showOnReports ? 'always' : 'never', field.required)
      : withoutConditionsOn(field, ids)));

  return {
    ...draft,
    fields: strip(draft.fields),
    groups: draft.groups.map((group) => ({ ...withoutConditionsOn(group, ids), fields: strip(group.fields) })),
  };
};

/** Removes a group with its fields, and the conditions that pointed at those fields. */
export const removeGroup = (draft: Draft, id: string): Draft => {
  const group = draft.groups.find((entry) => entry.id === id);
  const without = { ...draft, groups: draft.groups.filter((entry) => entry.id !== id) };

  return removeFields(without, (group?.fields ?? []).flatMap((field) => (field.id ? [field.id] : [])));
};

/** The ranges of a list that move together: each top-level field with everything under it. */
const blocksOf = (fields: PoiField[]) => {
  const starts = buildTree(fields).map((root) => root.index);

  return starts.map((start, index) => fields.slice(start, starts[index + 1] ?? fields.length));
};

/** Moves a top-level field together with everything drawn under it. */
export const moveBlock = (draft: Draft, container: Container, from: number, to: number): Draft =>
  withFieldsIn(draft, container, arrayMove(blocksOf(fieldsIn(draft, container)), from, to).flat());

/** Moves a field, with everything drawn under it, to the end of another list. */
export const moveField = (draft: Draft, id: string, target: Container): Draft => {
  const at = locate(draft, id);

  if (!at || at.container === target) return draft;

  const fields = fieldsIn(draft, at.container);
  const end = blockEnd(fields, at.index);
  const without = withFieldsIn(draft, at.container, [...fields.slice(0, at.index), ...fields.slice(end)]);

  return withFieldsIn(without, target, [...fieldsIn(without, target), ...fields.slice(at.index, end)]);
};

export const moveGroup = (draft: Draft, from: number, to: number): Draft =>
  ({ ...draft, groups: arrayMove(draft.groups, from, to) });

/** A status by the name every screen and report gives it, so an author reads one word everywhere. */
export const STEP_STATUS_LABELS: Record<typeof stepStatuses[number], string> = noticeLabelKeys;

/**
 * The step's status as an entry of the picker: what a condition can ask about that no type
 * defines. Its four names stand where a field's choices would, so every editor offers,
 * draws and describes it the way it does a choice. Their labels are interface text and not
 * an author's, so each is a map of one entry, which every reader's fallback finds.
 */
export const stepStatusCandidate = (t: (key: string) => string): RuleCandidate => ({
  id: STEP_STATUS_ID,
  label: t('Customization_StepStatus'),
  type: 'StepStatus',
  options: stepStatuses.map((status) => ({ id: status, label: { interface: t(STEP_STATUS_LABELS[status]) } })),
  source: t('Customization_RuleSourceInspection'),
});

/**
 * What a condition can point at: the type's own fields first, then every ancestor's, so an own
 * field shadows an ancestor's sharing its id the way the server resolves it, and the step's
 * status last. Last, because a new condition starts on the first entry, and that stays a
 * field wherever the type has one.
 */
export const candidatesOf = (
  draft: Draft, inherited: ResolvedItem[], resolve: (text?: LocalizedText) => string, unnamed: string,
  status: RuleCandidate,
): RuleCandidate[] => {
  const found = new Map<string, RuleCandidate>();
  const add = (field: PoiField, source: string | null, groupName?: string) => {
    if (!field.id || found.has(field.id)) return;

    found.set(field.id, {
      id: field.id, label: resolve(field.label) || unnamed, type: field.type, options: field.options,
      decimal: field.decimal, min: field.min, max: field.max, source, groupName,
    });
  };

  draft.fields.forEach((field) => add(field, null));
  draft.groups.forEach((group) => group.fields.forEach((field) => add(field, null, resolve(group.name))));
  inherited.forEach((entry) => {
    const source = resolve(entry.inheritedFrom?.name);

    if (entry.kind === 'field') add(entry.field, source);
    else entry.group.fields.forEach((field) => add(field, source, resolve(entry.group.name)));
  });

  return [...found.values(), status];
};

/** How many follow-ups each answer of `trigger` opens, keyed like `followUpAnswers`. */
export const followUpCounts = (draft: Draft, trigger: PoiField): Map<string, number> => {
  const counts = new Map<string, number>();

  for (const entry of [...allFieldsOf(draft), ...draft.groups]) {
    if (!trigger.id || triggerOf(entry) !== trigger.id) continue;

    for (const condition of entry.rules?.[0]?.when.conditions ?? []) {
      answerKeysOf(condition, trigger.type).forEach((key) => counts.set(key, (counts.get(key) ?? 0) + 1));
    }
  }

  return counts;
};

/** Labels without text in `locale`: field questions, choices and group names. */
export const untranslatedCount = (draft: Draft, locale: string): number => [
  ...allFieldsOf(draft).flatMap((field) => [field.label, ...(field.options ?? []).map((option) => option.label)]),
  ...draft.groups.map((group) => group.name),
].filter((text) => !text?.[locale]?.trim()).length;

/** What stops a draft from saving, and where. */
export type DraftIssue =
  | { kind: 'field' | 'group'; id?: string; problem: 'unnamed' | 'incomplete' }
  | { kind: 'field' | 'group'; id?: string; problem: ConditionProblem; entry: PoiField | PoiFieldGroup; condition: PoiFieldCondition }
  | { kind: 'type'; problem: 'unnamed' };

/**
 * Everything the save would be refused for or would quietly change: a missing name or choice
 * text, a condition still being written, and a condition the save path would prune. Pruning
 * is how a deleted field's conditions go, but a condition typed with a wrong id, operator or
 * choice would vanish the same way and leave the field it showed hidden for good.
 */
export const draftIssues = (draft: Draft, candidates: ReadonlyMap<string, RuleCandidate>): DraftIssue[] => {
  const entries: { kind: 'field' | 'group'; entry: PoiField | PoiFieldGroup }[] = [
    ...allFieldsOf(draft).map((entry) => ({ kind: 'field' as const, entry })),
    ...draft.groups.map((entry) => ({ kind: 'group' as const, entry })),
  ];

  return [
    ...(draft.name && !hasText(draft.name) ? [{ kind: 'type' as const, problem: 'unnamed' as const }] : []),
    ...entries.flatMap(({ kind, entry }): DraftIssue[] => {
      const named = 'label' in entry
        ? hasText(entry.label) && (entry.options ?? []).every((option) => hasText(option.label))
        : hasText(entry.name);
      const rules = entry.rules ?? [];
      const conditions = rules.flatMap((rule) => rule.when.conditions);
      // The API refuses a rule without conditions, and a condition without its value.
      const complete = rules.every((rule) => rule.when.conditions.length) && conditions.every(conditionComplete);

      return [
        ...(named ? [] : [{ kind, id: entry.id, problem: 'unnamed' as const }]),
        ...(complete ? [] : [{ kind, id: entry.id, problem: 'incomplete' as const }]),
        ...conditions.filter(conditionComplete).flatMap((condition) => {
          const problem = conditionProblem(condition, candidates.get(condition.fieldId));

          return problem ? [{ kind, id: entry.id, problem, entry, condition }] : [];
        }),
      ];
    }),
  ];
};
